import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import NavigationBar from '../../components/ui/NavigationBar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { whiteLabelService } from '../../services/whiteLabelService';
import FeatureGate from '../../components/plan/FeatureGate';

// ─── Shared helpers ────────────────────────────────────────────────────────────
const Section = ({ icon, title, description, children }) => (
  <div className="bg-card border border-border rounded-lg overflow-hidden">
    <div className="px-6 py-5 border-b border-border flex items-start gap-4">
      <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon name={icon} size={18} color="var(--color-primary)" />
      </div>
      <div>
        <h2 className="text-base font-heading font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

const AssetUploader = ({ label, hint, currentUrl, onUpload, accept = 'image/*', uploading }) => {
  const inputRef = useRef(null);
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
          {currentUrl ? (
            <img src={currentUrl} alt={label} className="w-full h-full object-contain p-1" />
          ) : (
            <Icon name="ImagePlus" size={22} color="var(--color-muted-foreground)" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input ref={inputRef} type="file" accept={accept} className="hidden"
            onChange={(e) => e?.target?.files?.[0] && onUpload(e?.target?.files?.[0])} />
          <Button variant="outline" size="sm" iconName={uploading ? 'Loader2' : 'Upload'} iconPosition="left"
            onClick={() => inputRef?.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : currentUrl ? 'Replace' : 'Upload'}
          </Button>
          {currentUrl && <p className="text-xs text-muted-foreground mt-1.5 truncate max-w-xs">{currentUrl}</p>}
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

const CnameBadge = ({ verified }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${verified ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
    <Icon name={verified ? 'CheckCircle2' : 'Clock'} size={12} />
    {verified ? 'Verified' : 'Pending verification'}
  </span>
);

// ─── Tab: Branding ─────────────────────────────────────────────────────────────
const BrandingTab = ({ settings, onChange, onUpload, uploadingLogo, uploadingFavicon }) => (
  <div className="space-y-6">
    <Section icon="Layers" title="White-Label Mode"
      description="Remove all WebForms.biz references from embed codes, hosted form links, and customer-facing footers.">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Enable white-label mode</p>
          <p className="text-xs text-muted-foreground mt-0.5">Removes all WebForms.biz branding from client-facing surfaces</p>
        </div>
        <button type="button" onClick={() => onChange('isEnabled', !settings?.isEnabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${settings?.isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
          aria-label="Toggle white-label mode">
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${settings?.isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
      {settings?.isEnabled && (
        <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-md">
          <p className="text-xs text-primary font-medium flex items-center gap-1.5">
            <Icon name="Info" size={13} />
            White-label is active. Embed codes and hosted form links will use your custom domain.
          </p>
        </div>
      )}
    </Section>

    <Section icon="Palette" title="Brand Assets"
      description="Upload your agency logo and favicon to replace WebForms.biz branding.">
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <AssetUploader label="Agency Logo" hint="PNG, JPG or WebP · Max 2 MB · Recommended 200×60px"
            currentUrl={settings?.logoUrl} onUpload={(f) => onUpload(f, 'logo')} uploading={uploadingLogo} />
          <AssetUploader label="Favicon" hint="ICO, PNG or WebP · Max 2 MB · 32×32px or 64×64px"
            currentUrl={settings?.faviconUrl} onUpload={(f) => onUpload(f, 'favicon')}
            accept=".ico,.png,.webp,.gif" uploading={uploadingFavicon} />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Agency Name</label>
          <Input type="text" placeholder="Acme Agency" value={settings?.agencyName}
            onChange={(e) => onChange('agencyName', e?.target?.value)} />
          <p className="text-xs text-muted-foreground">Used in email footers and client-facing surfaces</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Primary Theme Color</label>
          <div className="flex items-center gap-3">
            <input type="color" value={settings?.primaryColor}
              onChange={(e) => onChange('primaryColor', e?.target?.value)}
              className="w-10 h-10 rounded-md border border-border cursor-pointer bg-transparent p-0.5" />
            <Input type="text" value={settings?.primaryColor}
              onChange={(e) => onChange('primaryColor', e?.target?.value)}
              placeholder="#107570" className="w-36 font-mono text-sm" maxLength={7} />
            <div className="w-8 h-8 rounded-md border border-border flex-shrink-0"
              style={{ backgroundColor: settings?.primaryColor }} />
            <p className="text-xs text-muted-foreground">Applied to buttons, links, and accents on client-facing forms</p>
          </div>
        </div>
      </div>
    </Section>

    <Section icon="Mail" title="Custom Email Footer"
      description="Replace the default WebForms.biz footer in all outgoing notification emails.">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Custom Footer Text
            <span className="ml-1.5 text-xs text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea rows={3}
            placeholder={`Powered by ${settings?.agencyName || 'Your Agency'} · Unsubscribe`}
            value={settings?.emailFooterText}
            onChange={(e) => onChange('emailFooterText', e?.target?.value)}
            className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none transition-colors" />
          <p className="text-xs text-muted-foreground">Plain text only. Leave blank to use "Powered by [Agency Name]".</p>
        </div>
        <div className="border border-border rounded-md overflow-hidden">
          <div className="px-4 py-2 bg-muted border-b border-border flex items-center gap-2">
            <Icon name="Eye" size={14} color="var(--color-muted-foreground)" />
            <span className="text-xs font-medium text-muted-foreground">Footer Preview</span>
          </div>
          <div className="px-4 py-4 bg-card text-center">
            <p className="text-xs text-muted-foreground">
              {settings?.emailFooterText || (settings?.agencyName ? `Powered by ${settings?.agencyName}` : 'Powered by WebForms.biz')}
            </p>
          </div>
        </div>
      </div>
    </Section>
  </div>
);

// ─── Tab: Custom Domain ────────────────────────────────────────────────────────
const CustomDomainTab = ({ settings, onChange }) => (
  <div className="space-y-6">
    <Section icon="Globe" title="Custom Domain (CNAME)"
      description="Map your own subdomain so forms are served from your agency's domain.">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Custom subdomain</label>
          <div className="flex gap-3 flex-wrap">
            <Input type="text" placeholder="forms.youragency.com" value={settings?.customDomain}
              onChange={(e) => onChange('customDomain', e?.target?.value)} className="flex-1 min-w-0" />
            {settings?.customDomain && <CnameBadge verified={settings?.cnameVerified} />}
          </div>
          <p className="text-xs text-muted-foreground">
            Enter the subdomain you want to use (e.g. <code className="bg-muted px-1 rounded text-xs">forms.youragency.com</code>)
          </p>
        </div>
        {settings?.customDomain && (
          <div className="bg-muted rounded-md p-4 space-y-3">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider">DNS Configuration Required</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-muted-foreground font-medium">Type</div>
              <div className="text-muted-foreground font-medium">Name</div>
              <div className="text-muted-foreground font-medium">Value</div>
              <div className="font-mono text-foreground bg-card border border-border rounded px-2 py-1">CNAME</div>
              <div className="font-mono text-foreground bg-card border border-border rounded px-2 py-1 truncate">
                {settings?.customDomain?.split('.')?.[0] || '@'}
              </div>
              <div className="font-mono text-foreground bg-card border border-border rounded px-2 py-1 truncate">forms.webforms.biz</div>
            </div>
            <p className="text-xs text-muted-foreground">Add this CNAME record to your DNS provider. Propagation can take up to 48 hours.</p>
          </div>
        )}
      </div>
    </Section>

    {settings?.isEnabled && settings?.customDomain && (
      <Section icon="Code2" title="White-Label Embed Preview"
        description="When white-label mode is active, embed codes use your custom domain instead of WebForms.biz.">
        <div className="space-y-3">
          <div className="relative bg-muted rounded-md overflow-hidden">
            <div className="flex items-center px-4 py-2 border-b border-border">
              <span className="text-xs font-mono font-medium text-yellow-500">JAVASCRIPT</span>
            </div>
            <div className="p-4 overflow-x-auto">
              <pre className="text-xs font-mono text-foreground whitespace-pre">{`<script src="https://${settings?.customDomain}/embed.js"></script>
<script>
  Forms.embed({
    formId: 'wf_abc123xyz',
    container: '#form-container',
  });
</script>
<div id="form-container"></div>`}</pre>
            </div>
          </div>
          <div className="relative bg-muted rounded-md overflow-hidden">
            <div className="flex items-center px-4 py-2 border-b border-border">
              <span className="text-xs font-mono font-medium text-orange-500">HTML (iFrame)</span>
            </div>
            <div className="p-4 overflow-x-auto">
              <pre className="text-xs font-mono text-foreground whitespace-pre">{`<iframe
  src="https://${settings?.customDomain}/f/wf_abc123xyz"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Contact Form">
</iframe>`}</pre>
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Icon name="Info" size={12} />
            These codes are shown to your clients in the Embed Settings page when white-label mode is active.
          </p>
        </div>
      </Section>
    )}

    <Section icon="ShieldCheck" title="SSL Certificate"
      description="SSL is automatically provisioned once your CNAME record is verified.">
      <div className="flex items-center gap-4 p-4 bg-muted rounded-md">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${settings?.cnameVerified ? 'bg-success/10' : 'bg-muted-foreground/10'}`}>
          <Icon name={settings?.cnameVerified ? 'Lock' : 'LockOpen'} size={20}
            color={settings?.cnameVerified ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">
            {settings?.cnameVerified ? 'SSL Active' : 'SSL Pending'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {settings?.cnameVerified
              ? `HTTPS is active for ${settings?.customDomain}`
              : 'SSL will be provisioned automatically after CNAME verification completes.'}
          </p>
        </div>
      </div>
    </Section>
  </div>
);

// ─── Tab: Embed Styling ────────────────────────────────────────────────────────
const EmbedStylingTab = ({ embedStyle, onEmbedChange }) => {
  const borderRadiusOptions = ['0px', '4px', '8px', '12px', '16px', '24px'];
  const fontOptions = ['DM Sans', 'Manrope', 'Plus Jakarta Sans', 'General Sans', 'System Default'];
  const shadowOptions = [
    { label: 'None', value: 'none' },
    { label: 'Subtle', value: '0 1px 3px rgba(0,0,0,0.08)' },
    { label: 'Medium', value: '0 4px 16px rgba(0,0,0,0.12)' },
    { label: 'Elevated', value: '0 8px 32px rgba(0,0,0,0.16)' },
  ];

  return (
    <div className="space-y-6">
      <Section icon="PaintBucket" title="Form Container Styling"
        description="Control how embedded forms appear on your clients' websites.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Background Color</label>
            <div className="flex items-center gap-3">
              <input type="color" value={embedStyle?.bgColor}
                onChange={(e) => onEmbedChange('bgColor', e?.target?.value)}
                className="w-10 h-10 rounded-md border border-border cursor-pointer bg-transparent p-0.5" />
              <Input type="text" value={embedStyle?.bgColor}
                onChange={(e) => onEmbedChange('bgColor', e?.target?.value)}
                className="flex-1 font-mono text-sm" maxLength={7} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Border Color</label>
            <div className="flex items-center gap-3">
              <input type="color" value={embedStyle?.borderColor}
                onChange={(e) => onEmbedChange('borderColor', e?.target?.value)}
                className="w-10 h-10 rounded-md border border-border cursor-pointer bg-transparent p-0.5" />
              <Input type="text" value={embedStyle?.borderColor}
                onChange={(e) => onEmbedChange('borderColor', e?.target?.value)}
                className="flex-1 font-mono text-sm" maxLength={7} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Border Radius</label>
            <div className="flex flex-wrap gap-2">
              {borderRadiusOptions?.map((r) => (
                <button key={r} type="button"
                  onClick={() => onEmbedChange('borderRadius', r)}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${embedStyle?.borderRadius === r ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Box Shadow</label>
            <div className="flex flex-col gap-2">
              {shadowOptions?.map((s) => (
                <button key={s?.value} type="button"
                  onClick={() => onEmbedChange('boxShadow', s?.value)}
                  className={`flex items-center gap-2 px-3 py-2 text-xs rounded-md border transition-colors text-left ${embedStyle?.boxShadow === s?.value ? 'bg-primary/10 border-primary text-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'}`}>
                  <div className="w-8 h-5 rounded bg-card flex-shrink-0" style={{ boxShadow: s?.value === 'none' ? 'none' : s?.value }} />
                  {s?.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section icon="Type" title="Typography"
        description="Set the font family used across all embedded forms.">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Font Family</label>
            <div className="flex flex-wrap gap-2">
              {fontOptions?.map((f) => (
                <button key={f} type="button"
                  onClick={() => onEmbedChange('fontFamily', f)}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${embedStyle?.fontFamily === f ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Base Font Size</label>
            <div className="flex items-center gap-3">
              <input type="range" min="12" max="20" step="1"
                value={parseInt(embedStyle?.fontSize) || 14}
                onChange={(e) => onEmbedChange('fontSize', `${e?.target?.value}px`)}
                className="flex-1 accent-primary" />
              <span className="text-sm font-mono text-foreground w-10 text-right">{embedStyle?.fontSize}</span>
            </div>
          </div>
        </div>
      </Section>

      <Section icon="Eye" title="Live Preview"
        description="See how your embedded form styling will look to end users.">
        <div className="rounded-lg overflow-hidden border border-border"
          style={{ boxShadow: embedStyle?.boxShadow !== 'none' ? embedStyle?.boxShadow : undefined }}>
          <div className="p-6"
            style={{
              backgroundColor: embedStyle?.bgColor,
              borderRadius: embedStyle?.borderRadius,
              fontFamily: embedStyle?.fontFamily !== 'System Default' ? embedStyle?.fontFamily : undefined,
              fontSize: embedStyle?.fontSize,
              border: `1px solid ${embedStyle?.borderColor}`,
            }}>
            <p className="font-semibold text-foreground mb-4" style={{ fontSize: embedStyle?.fontSize }}>Contact Us</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Full Name</label>
                <div className="h-9 rounded-md border border-border bg-background px-3 flex items-center">
                  <span className="text-xs text-muted-foreground">Jane Smith</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Email</label>
                <div className="h-9 rounded-md border border-border bg-background px-3 flex items-center">
                  <span className="text-xs text-muted-foreground">jane@example.com</span>
                </div>
              </div>
              <div className="h-9 rounded-md flex items-center justify-center text-xs font-medium text-white"
                style={{ backgroundColor: 'var(--color-primary)', borderRadius: embedStyle?.borderRadius }}>
                Submit
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

// ─── Tab: Customer Customization ───────────────────────────────────────────────
const CustomerCustomizationTab = ({ customerConfig, onCustomerChange }) => (
  <div className="space-y-6">
    <Section icon="UserCog" title="Customer Portal Branding"
      description="Customize what your end-customers see when they interact with forms.">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Portal Title</label>
          <Input type="text" placeholder="My Agency Forms" value={customerConfig?.portalTitle}
            onChange={(e) => onCustomerChange('portalTitle', e?.target?.value)} />
          <p className="text-xs text-muted-foreground">Shown in the browser tab and form header area</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Support Email</label>
          <Input type="email" placeholder="support@youragency.com" value={customerConfig?.supportEmail}
            onChange={(e) => onCustomerChange('supportEmail', e?.target?.value)} />
          <p className="text-xs text-muted-foreground">Displayed in form error messages and confirmation emails</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Privacy Policy URL</label>
          <Input type="url" placeholder="https://youragency.com/privacy" value={customerConfig?.privacyUrl}
            onChange={(e) => onCustomerChange('privacyUrl', e?.target?.value)} />
          <p className="text-xs text-muted-foreground">Linked in form footers and consent checkboxes</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Terms of Service URL</label>
          <Input type="url" placeholder="https://youragency.com/terms" value={customerConfig?.termsUrl}
            onChange={(e) => onCustomerChange('termsUrl', e?.target?.value)} />
        </div>
      </div>
    </Section>

    <Section icon="MessageSquare" title="Customer-Facing Messages"
      description="Customize the text customers see at key touchpoints.">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Form Submission Success Message</label>
          <textarea rows={2} placeholder="Thank you! Your submission has been received."
            value={customerConfig?.successMessage}
            onChange={(e) => onCustomerChange('successMessage', e?.target?.value)}
            className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none transition-colors" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Form Error Message</label>
          <textarea rows={2} placeholder="Something went wrong. Please try again or contact support."
            value={customerConfig?.errorMessage}
            onChange={(e) => onCustomerChange('errorMessage', e?.target?.value)}
            className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none transition-colors" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Loading / Processing Text</label>
          <Input type="text" placeholder="Submitting your form…" value={customerConfig?.loadingText}
            onChange={(e) => onCustomerChange('loadingText', e?.target?.value)} />
        </div>
      </div>
    </Section>

    <Section icon="ToggleLeft" title="Feature Visibility"
      description="Control which elements are visible to end-customers on embedded forms.">
      <div className="space-y-4">
        {[
          { key: 'showPoweredBy', label: 'Show "Powered by" attribution', desc: 'Displays your agency name in the form footer' },
          { key: 'showProgressBar', label: 'Show progress bar on multi-step forms', desc: 'Displays step completion progress to users' },
          { key: 'showFormTitle', label: 'Show form title', desc: 'Displays the form name at the top of embedded forms' },
          { key: 'showRequiredIndicator', label: 'Show required field indicators', desc: 'Marks required fields with an asterisk (*)' },
        ]?.map((item) => (
          <div key={item?.key} className="flex items-center justify-between py-3 border-b border-border last:border-0">
            <div>
              <p className="text-sm font-medium text-foreground">{item?.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item?.desc}</p>
            </div>
            <button type="button" onClick={() => onCustomerChange(item?.key, !customerConfig?.[item?.key])}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 ml-4 ${customerConfig?.[item?.key] ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              aria-label={`Toggle ${item?.label}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${customerConfig?.[item?.key] ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        ))}
      </div>
    </Section>
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'branding', label: 'Branding', icon: 'Palette' },
  { id: 'domain', label: 'Custom Domain', icon: 'Globe' },
  { id: 'embed', label: 'Embed Styling', icon: 'Code2' },
  { id: 'customer', label: 'Customer Customization', icon: 'UserCog' },
];

const WhiteLabelConfigurationPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams?.get('tab') || 'branding');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const [settings, setSettings] = useState({
    isEnabled: false,
    customDomain: '',
    cnameVerified: false,
    logoUrl: '',
    faviconUrl: '',
    primaryColor: '#107570',
    emailFooterText: '',
    agencyName: '',
  });

  const [embedStyle, setEmbedStyle] = useState({
    bgColor: '#ffffff',
    borderColor: '#e2e8f0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    fontFamily: 'DM Sans',
    fontSize: '14px',
  });

  const [customerConfig, setCustomerConfig] = useState({
    portalTitle: '',
    supportEmail: '',
    privacyUrl: '/privacy',
    termsUrl: '/terms',
    successMessage: '',
    errorMessage: '',
    loadingText: '',
    showPoweredBy: true,
    showProgressBar: true,
    showFormTitle: true,
    showRequiredIndicator: true,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await whiteLabelService?.getSettings();
        if (data) {
          setSettings({
            isEnabled: data?.isEnabled ?? false,
            customDomain: data?.customDomain ?? '',
            cnameVerified: data?.cnameVerified ?? false,
            logoUrl: data?.logoUrl ?? '',
            faviconUrl: data?.faviconUrl ?? '',
            primaryColor: data?.primaryColor ?? '#107570',
            emailFooterText: data?.emailFooterText ?? '',
            agencyName: data?.agencyName ?? '',
          });
          if (data?.customerConfig && Object.keys(data.customerConfig)?.length > 0) {
            setCustomerConfig((prev) => ({ ...prev, ...data.customerConfig }));
          }
        }
      } catch (err) {
        console.error('Failed to load white-label settings:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => setSettings((prev) => ({ ...prev, [field]: value }));
  const handleEmbedChange = (field, value) => setEmbedStyle((prev) => ({ ...prev, [field]: value }));
  const handleCustomerChange = (field, value) => setCustomerConfig((prev) => ({ ...prev, [field]: value }));

  const handleUpload = async (file, type) => {
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);
    try {
      const url = await whiteLabelService?.uploadAsset(file, type);
      handleChange(type === 'logo' ? 'logoUrl' : 'faviconUrl', url);
    } catch (err) {
      setError(`Failed to upload ${type}: ${err?.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      await whiteLabelService?.saveSettings({ ...settings, customerConfig });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar stats={{}}>
          <div className="flex items-center justify-center h-64">
            <Icon name="Loader2" size={28} color="var(--color-primary)" className="animate-spin" />
          </div>
        </NavigationBar>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>White-Label Configuration — WebForms</title>
        <meta name="description" content="Configure white-label branding, custom domain, embedded form styling, and customer-facing customization." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <NavigationBar stats={{}}>
          <FeatureGate feature="white_label">
          <div className="w-full max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10">

            {/* Page header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <Icon name="Building2" size={14} />
                <span>Agency Portal</span>
                <Icon name="ChevronRight" size={14} />
                <span className="text-foreground font-medium">White-Label Configuration</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                      White-Label Configuration
                    </h1>
                    <span className="px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-primary/10 text-primary">
                      {settings?.isEnabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Centralized control for branding, domain, embed styling, and customer-facing customization.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {saveSuccess && (
                    <span className="flex items-center gap-1.5 text-sm text-success font-medium">
                      <Icon name="CheckCircle2" size={16} />
                      Saved
                    </span>
                  )}
                  <Button variant="primary" size="default" iconName={saving ? 'Loader2' : 'Save'}
                    iconPosition="left" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 flex items-start gap-3 px-4 py-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
                <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Tabs */}
            <div className="border-b border-border mb-8">
              <nav className="flex gap-1 overflow-x-auto scrollbar-custom -mb-px">
                {TABS?.map((tab) => (
                  <button key={tab?.id} type="button" onClick={() => handleTabChange(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}>
                    <Icon name={tab?.icon} size={16} color={activeTab === tab?.id ? 'var(--color-primary)' : 'currentColor'} />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab content */}
            {activeTab === 'branding' && (
              <BrandingTab settings={settings} onChange={handleChange}
                onUpload={handleUpload} uploadingLogo={uploadingLogo} uploadingFavicon={uploadingFavicon} />
            )}
            {activeTab === 'domain' && (
              <CustomDomainTab settings={settings} onChange={handleChange} />
            )}
            {activeTab === 'embed' && (
              <EmbedStylingTab embedStyle={embedStyle} onEmbedChange={handleEmbedChange} />
            )}
            {activeTab === 'customer' && (
              <CustomerCustomizationTab customerConfig={customerConfig} onCustomerChange={handleCustomerChange} />
            )}

            {/* Bottom save bar */}
            <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Changes apply immediately to all client-facing surfaces.</p>
              <div className="flex items-center gap-3">
                {saveSuccess && (
                  <span className="flex items-center gap-1.5 text-sm text-success font-medium">
                    <Icon name="CheckCircle2" size={16} />
                    Saved
                  </span>
                )}
                <Button variant="primary" size="default" iconName={saving ? 'Loader2' : 'Save'}
                  iconPosition="left" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </div>

          </div>
          </FeatureGate>
        </NavigationBar>
      </div>
    </>
  );
};

export default WhiteLabelConfigurationPage;
